import classNames from 'classnames'
import { type CSSProperties, forwardRef, useState } from 'react'
import { VirtualList } from 'shared/ui/VirtualList'
import { type IScrollbarsPropsRef } from 'shared/ui/Scrollbar/hooks/useScrollbarApiRef'
import { PlainScrollbar } from './PlainScrollbar'
import { type IScrollbarProps } from './types'
import styles from './style.module.scss'

export const VirtualizedScrollbar = forwardRef<IScrollbarsPropsRef, IScrollbarProps>(
  (
    {
      children,
      className,
      viewClassName,
      height,
      autoHeight,
      autoHeightMin,
      autoHeightMax,
      style,
      estimateSize,
      overscan,
      gap,
      paddingStart,
      paddingEnd,
      onEndReached,
      endReachedThreshold,
      onStartReached,
      startReachedThreshold,
      anchorTo,
      followOnAppend,
      scrollEndThreshold,
      directDomUpdates,
      useAnimationFrameWithResizeObserver,
      header,
      footer,
      contentWrapperClassName,
      contentClassName,
      measureItems,
      flushSyncOnScroll,
      skeletonWhileScrolling,
      renderSkeleton,
      virtualListRef,
      onViewRef,
      ...rest
    },
    ref
  ) => {
    const [view, setView] = useState<HTMLElement | null>(null)
    const handleViewRef = (node: HTMLElement | null) => {
      setView(node)
      onViewRef?.(node)
    }

    const rootStyle: CSSProperties = autoHeight
      ? { minHeight: autoHeightMin, maxHeight: autoHeightMax, ...style }
      : { height: height ?? '100%', ...style }

    return (
      <PlainScrollbar
        {...rest}
        ref={ref}
        className={className}
        viewClassName={classNames(styles.virtualizedView, viewClassName)}
        style={rootStyle}
        height={height}
        autoHeight={autoHeight}
        autoHeightMin={autoHeightMin}
        autoHeightMax={autoHeightMax}
        onViewRef={handleViewRef}
      >
        <VirtualList
          ref={virtualListRef}
          ariaLabel={'Scrollbar'}
          scrollElement={view}
          estimateSize={estimateSize}
          overscan={overscan}
          gap={gap}
          paddingStart={paddingStart}
          paddingEnd={paddingEnd}
          onEndReached={onEndReached}
          endReachedThreshold={endReachedThreshold}
          onStartReached={onStartReached}
          startReachedThreshold={startReachedThreshold}
          anchorTo={anchorTo}
          followOnAppend={followOnAppend}
          scrollEndThreshold={scrollEndThreshold}
          directDomUpdates={directDomUpdates}
          useAnimationFrameWithResizeObserver={useAnimationFrameWithResizeObserver}
          measureItems={measureItems}
          flushSyncOnScroll={flushSyncOnScroll}
          skeletonWhileScrolling={skeletonWhileScrolling}
          renderSkeleton={renderSkeleton}
          header={header}
          footer={footer}
          contentWrapperClassName={contentWrapperClassName}
          contentClassName={contentClassName}
        >
          {children}
        </VirtualList>
      </PlainScrollbar>
    )
  }
)

VirtualizedScrollbar.displayName = 'VirtualizedScrollbar'
